import { Matrix, Mesh, Scene, SubMesh, Vector3, VertexData } from '@babylonjs/core';

export class MeshBuilder {
    // Define rotation matrices for cube sides
    private static readonly _rotationFront = Matrix.Identity();
    private static readonly _rotationBack = Matrix.RotationY(Math.PI);
    private static readonly _rotationLeft = Matrix.RotationY(-Math.PI / 2);
    private static readonly _rotationRight = Matrix.RotationY(Math.PI / 2);
    private static readonly _rotationUp = Matrix.RotationX(-Math.PI / 2);
    private static readonly _rotationDown = Matrix.RotationX(Math.PI / 2);

    // Define cube sides with labels and corresponding rotation matrices
    public static readonly cubeSides = [
        { Label: 'l', RotationMatrix: MeshBuilder._rotationLeft },
        { Label: 'f', RotationMatrix: MeshBuilder._rotationFront },
        { Label: 'r', RotationMatrix: MeshBuilder._rotationRight },
        { Label: 'b', RotationMatrix: MeshBuilder._rotationBack },
        { Label: 'u', RotationMatrix: MeshBuilder._rotationUp },
        { Label: 'd', RotationMatrix: MeshBuilder._rotationDown }
    ];

    // Create a cube strip mesh
    public static CreateCubeStrip(
        name: string,
        options: {
            width?: number,
            height?: number,
            subdivisions?: number,
            subdivisionsX?: number,
            subdivisionsY?: number,
        },
        scene: Scene
    ): Mesh {
        const cubestrip = new Mesh(name, scene);

        // Create vertex data for tiled ground
        const vertexData = MeshBuilder.CreateTiledGround({
            precision: 1,
            subdivisions: options.subdivisions,
            size: options.width
        });
        vertexData.applyToMesh(cubestrip, false);

        // Create submeshes for the cube strip
        const submeshes = [];

        const cubeSideLength: number = MeshBuilder.cubeSides.length;
        const verticesCount = cubestrip.getTotalVertices();
        const subdivisionsX: number = options.subdivisionsX || options.subdivisions || 1;
        const subdivisionsY: number = options.subdivisionsY || options.subdivisions || 1;
        const tileIndicesLength =
            cubestrip.getIndices().length / (subdivisionsX * subdivisionsY) / cubeSideLength;
        const tileVerticesLength =
            verticesCount / (subdivisionsX * subdivisionsY) / cubeSideLength;

        let base = 0;
        let baseVertice = 0;
        let materialIndex = 0;
        for (let cubeSideIndex = 0; cubeSideIndex < cubeSideLength; cubeSideIndex++) {
            for (let row = 0; row < subdivisionsY; row++) {
                for (let col = 0; col < subdivisionsX; col++) {
                    let submesh = new SubMesh(
                        materialIndex++,
                        baseVertice,
                        tileVerticesLength,
                        base,
                        tileIndicesLength,
                        cubestrip,
                        null,
                        true
                    );

                    submeshes.push(submesh);
                    base += tileIndicesLength;
                    baseVertice += tileVerticesLength;
                }
            }
        }

        // Assign submeshes to the cube strip
        cubestrip.subMeshes = submeshes;
        cubestrip.freezeWorldMatrix();
        return cubestrip;
    }

    public static CreateTiledGround(
        options: {
            size: number,
            subdivisions?: number,
            precision?: number
        }
    ): VertexData {
        const xmin = -options.size / 2 || -1.0;
        const ymin = -options.size / 2 || -1.0;
        const xmax = options.size / 2 || 1.0;
        const ymax = options.size / 2 || 1.0;

        let subdivisions = options.subdivisions || 1.0;
        let precision = options.precision || 1.0;

        const indices = [];
        const positions = [];
        const normals = [];
        const uvs = [];

        subdivisions = (subdivisions < 1) ? 1 : subdivisions;
        precision = (precision < 1) ? 1 : precision;

        const tileSize = {
            'w': (xmax - xmin) / subdivisions,
            'h': (ymax - ymin) / subdivisions
        };

        //extract function
        function applyTile(xTileMin: number, yTileMin: number, xTileMax: number, yTileMax: number, transformationMatrix: Matrix) {
            // Indices
            const base = positions.length / 3;
            const rowLength = precision + 1;

            for (let row = 0; row < precision; row++) {
                for (let col = 0; col < precision; col++) {
                    const square = [
                        base + col + row * rowLength,
                        base + (col + 1) + row * rowLength,
                        base + (col + 1) + (row + 1) * rowLength,
                        base + col + (row + 1) * rowLength

                    ];

                    indices.push(square[1]);
                    indices.push(square[2]);
                    indices.push(square[3]);
                    indices.push(square[0]);
                    indices.push(square[1]);
                    indices.push(square[3]);
                }
            }

            // Position, normals and uvs
            let position = Vector3.Zero();
            let normal = Vector3.TransformNormal(new Vector3(0, 0, -1.0), transformationMatrix);

            for (let row = 0; row <= precision; row++) {
                for (let col = 0; col <= precision; col++) {
                    position.x = (col * (xTileMax - xTileMin)) / precision + xTileMin;
                    position.y = (row * (yTileMax - yTileMin)) / precision + yTileMin;
                    position.z = options.size / 2;
                    position = Vector3.TransformCoordinates(position, transformationMatrix);

                    positions.push(position.x, position.y, position.z);
                    normals.push(normal.x, normal.y, normal.z);
                    uvs.push(col / precision, row / precision);

                }
            }
        }

        for (const cubeSide of MeshBuilder.cubeSides) {
            for (let tileRow = 0; tileRow < subdivisions; tileRow++) {
                for (let tileCol = 0; tileCol < subdivisions; tileCol++) {
                    applyTile(
                        xmin + tileCol * tileSize.w,
                        ymin + tileRow * tileSize.h,
                        xmin + (tileCol + 1) * tileSize.w,
                        ymin + (tileRow + 1) * tileSize.h,
                        cubeSide.RotationMatrix
                    );
                }
            }
        }

        // Result
        const vertexData = new VertexData();

        vertexData.indices = indices;
        vertexData.positions = positions;
        vertexData.normals = normals;
        vertexData.uvs = uvs;

        return vertexData;
    }

    public static createSphere(name: string, scene: Scene): Mesh {
        const sphere = new Mesh(name, scene);
        const options = {
            diameter: 1304,
            sideOrientation: Mesh.BACKSIDE,
            widthSegments: 60,
            heightSegments: 30,
            updatable: false
        };

        const vertexData = MeshBuilder.createVertexDataSphere(options);
        vertexData.applyToMesh(sphere, options.updatable);

        return sphere;
    }

    private static createVertexDataSphere(
        options: {
            widthSegments?: number,
            heightSegments?: number,
            diameter?: number,
            sideOrientation?: number
        }
    ): VertexData {
        const widthSegments = Math.max(3, Math.floor(options.widthSegments) || 8);
        const heightSegments = Math.max(2, Math.floor(options.heightSegments) || 6);

        const radius = (options.diameter || 100) / 2;
        const phiStart = 0;
        const phiLength = Math.PI * 2;
        const thetaStart = 0;
        const thetaLength = Math.PI;

        const indexGrid: number[][] = [];
        const indices: number[] = [];
        const vertices: number[] = [];
        const normals: number[] = [];
        const uvs: number[] = [];

        for (let iy = 0; iy <= heightSegments; iy++) {
            const verticesRow: number[] = [];
            const v = iy / heightSegments;

            for (let ix = 0; ix <= widthSegments; ix++) {
                const u = ix / widthSegments;
                const vertex = new Vector3(
                    radius * Math.sin(phiStart + u * phiLength) * Math.sin(thetaStart + v * thetaLength),
                    radius * Math.cos(thetaStart + v * thetaLength),
                    -radius * Math.cos(phiStart + u * phiLength) * Math.sin(thetaStart + v * thetaLength)
                );

                vertices.push(vertex.x, vertex.y, vertex.z);
                const normal = vertex.normalize();
                normals.push(normal.x, normal.y, normal.z);
                uvs.push(u, 1 - v);
                verticesRow.push(vertices.length / 3 - 1);
            }

            indexGrid.push(verticesRow);
        }

        for (let iy = 0; iy < heightSegments; iy++) {
            for (let ix = 0; ix < widthSegments; ix++) {
                const a = indexGrid[iy][ix + 1];
                const b = indexGrid[iy][ix];
                const c = indexGrid[iy + 1][ix];
                const d = indexGrid[iy + 1][ix + 1];

                if (iy !== 0 || thetaStart > 0) indices.push(a, b, d);
                if (iy !== heightSegments - 1 || thetaStart + thetaLength < Math.PI) indices.push(b, c, d);
            }
        }

        for (let i = 0; i < indices.length; i += 3) {
            [indices[i], indices[i + 2]] = [indices[i + 2], indices[i]];
        }

        for (let n = 0; n < normals.length; n++) {
            normals[n] = -normals[n];
        }

        const vertexData = new VertexData();
        vertexData.indices = indices;
        vertexData.positions = vertices;
        vertexData.normals = normals;
        vertexData.uvs = uvs;

        return vertexData;
    }
}
